<script>
  import Icon from '../Icon.svelte';
  import { launchApp } from '../../os/windows.svelte.js';

  let { apps } = $props();

  const STORAGE_KEY = 'smacos.desktop-icons';

  let selectedId = $state(null);
  let positions = $state(loadPositions());
  let dragging = $state(null);
  let didDrag = false;

  function loadPositions() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    } catch { return {}; }
  }

  function savePositions() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  }

  function deselect() {
    selectedId = null;
  }

  function select(appId) {
    selectedId = appId;
  }

  function handleKeydown(e) {
    if (e.key === 'Enter' && selectedId) {
      const app = apps.find(a => a.id === selectedId);
      if (app) launchApp(app);
    }
    if (e.key === 'Escape') deselect();
  }

  function startDrag(appId, e) {
    if (e.button !== 0) return;
    select(appId);
    didDrag = false;
    const pos = positions[appId];
    dragging = {
      id: appId,
      startX: e.clientX,
      startY: e.clientY,
      originX: pos?.x,
      originY: pos?.y,
    };
    if (pos === undefined) {
      const rect = e.currentTarget.getBoundingClientRect();
      positions[appId] = { x: rect.left, y: rect.top };
      dragging.originX = rect.left;
      dragging.originY = rect.top;
    }
    globalThis.addEventListener('pointermove', onDrag);
    globalThis.addEventListener('pointerup', endDrag);
  }

  function onDrag(e) {
    if (!dragging) return;
    const dx = e.clientX - dragging.startX;
    const dy = e.clientY - dragging.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag = true;
    positions[dragging.id] = {
      x: (dragging.originX ?? 0) + dx,
      y: (dragging.originY ?? 0) + dy,
    };
  }

  function endDrag() {
    if (dragging) {
      savePositions();
      dragging = null;
    }
    globalThis.removeEventListener('pointermove', onDrag);
    globalThis.removeEventListener('pointerup', endDrag);
  }

  function iconStyle(app) {
    const pos = positions[app.id];
    if (!pos) return {};
    return { position: 'absolute', left: pos.x + 'px', top: pos.y + 'px' };
  }

  function handleClick(app, e) {
    if (didDrag) return;
    e.stopPropagation();
    select(app.id);
  }

  function handleDblClick(app, e) {
    e.stopPropagation();
    select(app.id);
    launchApp(app);
  }

  $effect(() => {
    function onWindowClick(e) {
      if (selectedId && !e.target.closest('.desk-icon')) deselect();
    }
    globalThis.addEventListener('click', onWindowClick);
    return () => globalThis.removeEventListener('click', onWindowClick);
  });
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="icons" style="--icon-count:{apps.length}">
  {#each apps as app}
    <button
      class="desk-icon"
      class:selected={selectedId === app.id}
      class:dragging={dragging?.id === app.id}
      style={iconStyle(app)}
      onclick={(e) => handleClick(app, e)}
      ondblclick={(e) => handleDblClick(app, e)}
      onpointerdown={(e) => startDrag(app.id, e)}
    >
      <span class="glyph"><Icon name={app.icon} size={46} /></span>
      <span class="ic-label">{app.label}</span>
    </button>
  {/each}
</div>

<style>
  .icons {
    position: absolute;
    top: 32px; right: 16px; bottom: 80px;
    z-index: 3;
    display: flex; flex-direction: column; flex-wrap: wrap;
    align-content: flex-end; gap: 16px;
  }
  .desk-icon {
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    width: 84px; background: none; border: 0; cursor: pointer; color: #fff; font-family: var(--font);
    padding: 4px 0;
    border-radius: 4px;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .desk-icon:hover {
    z-index: 1;
  }
  .desk-icon.dragging {
    opacity: 0.85;
    z-index: 100;
  }
  .desk-icon .glyph {
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.5));
    transition: transform 0.2s cubic-bezier(0.25, 0.8, 0.25, 1.4), filter 0.2s ease;
    pointer-events: none;
  }
  /* win98-style solid label chips with a hard outline */
  .desk-icon .ic-label {
    font-size: 12px; padding: 1px 6px; border-radius: 0;
    background: rgba(10, 14, 20, 0.82);
    outline: 1px solid rgba(0, 0, 0, 0.85);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
    text-shadow: none;
    pointer-events: none;
  }
  .desk-icon.selected .ic-label,
  .desk-icon:hover .ic-label {
    background: linear-gradient(var(--graphite-select-top), var(--graphite-select-bot));
    outline: 1px solid #000;
    box-shadow: 0 0 0 1px var(--graphite-select-edge), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  .desk-icon:hover .glyph {
    transform: scale(1.1) translateY(-3px); filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.6));
  }
  .desk-icon:active .glyph {
    transform: scale(0.92) translateY(0); transition: transform 0.05s ease;
  }
</style>
